import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import "./company-docs.css";

type Folder = {
  id: string;
  name: string;
  parent_id: string | null;
  document_count?: number;
  total_document_count?: number;
  access_roles?: string[] | null;
  access_users?: string[];
};
type Person = { id: string; name: string; role: string };
type Doc = {
  id: string;
  folder_id: string;
  name: string;
  description: string;
  tags: string[];
  status: string;
  owner_id: string | null;
  pinned: boolean;
  review_date: string | null;
  expires_on: string | null;
  version_count: number;
  updated_at: string;
  deleted_at: string | null;
};
type Version = {
  id: string;
  version_number: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  created_at: string;
};
const statuses = ["draft", "in_review", "approved", "superseded", "archived"];
const roles = [
  "specialist",
  "pm",
  "contracting_officer",
  "salesman",
  "estimator",
  "foreman",
];
const label = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const errorText = (e: any) =>
  e?.response?.data?.error ||
  e?.message ||
  "The request could not be completed. Please try again.";
const size = (n: number) =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;
const date = (s: string | null) =>
  s
    ? new Date(s.length === 10 ? s + "T12:00:00" : s).toLocaleDateString()
    : "—";
const previewTypes = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "text/plain",
];

export default function CompanyDocsPage() {
  const [folders, setFolders] = useState<Folder[]>([]),
    [people, setPeople] = useState<Person[]>([]),
    [manage, setManage] = useState(false);
  const [folderId, setFolderId] = useState<string | null>(null),
    [view, setView] = useState("home"),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState("");
  const [docs, setDocs] = useState<Doc[]>([]),
    [total, setTotal] = useState(0),
    [page, setPage] = useState(0),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [selected, setSelected] = useState<Doc | null>(null),
    [versions, setVersions] = useState<Version[]>([]),
    [preview, setPreview] = useState<{ url: string; type: string } | null>(
      null,
    ),
    [detailLoading, setDetailLoading] = useState(false);
  const [folderDialog, setFolderDialog] = useState<"create" | "edit" | null>(
      null,
    ),
    [folderName, setFolderName] = useState(""),
    [accessMode, setAccessMode] = useState("inherit"),
    [accessRoles, setAccessRoles] = useState<string[]>([]),
    [accessUsers, setAccessUsers] = useState<string[]>([]);
  const [edit, setEdit] = useState<Doc | null>(null),
    [tags, setTags] = useState("");
  const input = useRef<HTMLInputElement>(null),
    replacement = useRef<HTMLInputElement>(null),
    requestId = useRef(0),
    fileRequestId = useRef(0);
  const folder = folders.find((f) => f.id === folderId);
  const pathTo = useCallback(
    (id: string | null): Folder[] => {
      const out: Folder[] = [];
      const seen = new Set();
      const map = new Map(folders.map((f) => [f.id, f]));
      while (id && !seen.has(id)) {
        seen.add(id);
        const f = map.get(id);
        if (!f) break;
        out.unshift(f);
        id = f.parent_id;
      }
      return out;
    },
    [folders],
  );
  const reloadFolders = useCallback(async () => {
    const { data } = await api.get("/company-docs/folders");
    setFolders(data.folders);
    setManage(data.can_manage);
    if (data.can_manage) {
      const p = await api.get("/company-docs/people");
      setPeople(p.data);
    }
  }, []);
  const reloadDocs = useCallback(async () => {
    const rid = ++requestId.current;
    setLoading(true);
    try {
      const { data } = await api.get("/company-docs/documents", {
        params: {
          ...(folderId && !query ? { folder: folderId } : {}),
          view: view === "home" ? "recent" : view,
          q: query,
          status,
          page,
        },
      });
      if (rid === requestId.current) {
        setDocs(data.documents);
        setTotal(data.total);
      }
    } catch (e) {
      if (rid === requestId.current) setError(errorText(e));
    } finally {
      if (rid === requestId.current) setLoading(false);
    }
  }, [folderId, query, status, view, page]);
  useEffect(() => {
    reloadFolders().catch((e) => setError(errorText(e)));
  }, [reloadFolders]);
  useEffect(() => {
    const guard = requestId;
    setDocs([]);
    reloadDocs();
    return () => {
      guard.current++;
    };
  }, [reloadDocs]);
  useEffect(() => {
    let live = true;
    setVersions([]);
    setDetailLoading(!!selected);
    if (selected)
      api
        .get(`/company-docs/documents/${selected.id}/versions`)
        .then(async ({ data }) => {
          if (!live) return;
          const loadedVersions = data as Version[];
          setVersions(loadedVersions);
          setDetailLoading(false);
          const latestPreviewable = loadedVersions.find((version) =>
            previewTypes.includes(version.mime_type),
          );
          if (!latestPreviewable || selected.deleted_at) return;
          const rid = ++fileRequestId.current;
          try {
            const response = await api.get(
              `/company-docs/documents/${selected.id}/file/${latestPreviewable.id}`,
              { params: { preview: true }, responseType: "blob" },
            );
            if (!live || rid !== fileRequestId.current) return;
            setPreview({
              url: URL.createObjectURL(
                new Blob([response.data], {
                  type: latestPreviewable.mime_type,
                }),
              ),
              type: latestPreviewable.mime_type,
            });
          } catch (e) {
            if (live && rid === fileRequestId.current) setError(errorText(e));
          }
        })
        .catch((e) => {
          if (live) setError(errorText(e));
        })
        .finally(() => {
          if (live) setDetailLoading(false);
        });
    return () => {
      live = false;
    };
  }, [selected]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  useEffect(
    () => () => {
      fileRequestId.current++;
    },
    [],
  );
  const navigate = (id: string | null, nextView = "folder") => {
    setFolderId(id);
    setView(nextView);
    setQuery("");
    setPage(0);
    setError("");
  };
  const openDoc = (doc: Doc) => {
    fileRequestId.current++;
    setPreview(null);
    setSelected(doc);
    setEdit({ ...doc });
    setTags(doc.tags.join(", "));
  };
  const closeDoc = () => {
    fileRequestId.current++;
    setSelected(null);
    setEdit(null);
    setPreview(null);
  };
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const patchDoc = async (doc: Doc, patch: object) => {
    const { data } = await api.patch(`/company-docs/documents/${doc.id}`, {
      updated_at: doc.updated_at,
      ...patch,
    });
    await Promise.all([reloadDocs(), reloadFolders()]);
    return data as Doc;
  };
  const uploadFiles = (files: FileList | null, replace = false) =>
    run(async () => {
      if (!files?.length) return;
      if (!replace && !folderId) throw new Error("Select a folder first.");
      let count = 0;
      try {
        for (const file of Array.from(files)) {
          if (file.size > 50 * 1024 * 1024) {
            setError(`${file.name} is larger than 50 MB.`);
            break;
          }
          const form = new FormData();
          form.append("file", file);
          if (folderId) form.append("folder_id", folderId);
          await api.post(
            replace
              ? `/company-docs/documents/${selected!.id}/versions`
              : "/company-docs/documents",
            form,
          );
          count++;
        }
      } finally {
        await Promise.all([reloadDocs(), reloadFolders()]);
        if (replace && count) closeDoc();
        if (input.current) input.current.value = "";
        if (replacement.current) replacement.current.value = "";
        if (count)
          setNotice(
            `${count} ${replace ? "new version" : "file" + (count === 1 ? "" : "s")} uploaded.`,
          );
      }
    });
  const downloadFile = (version: Version) =>
    run(async () => {
      if (!selected) return;
      const rid = ++fileRequestId.current;
      const response = await api.get(
        `/company-docs/documents/${selected.id}/file/${version.id}`,
        { params: { preview: false }, responseType: "blob" },
      );
      if (rid !== fileRequestId.current) return;
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/octet-stream",
        }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = version.file_name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  const openFolderDialog = (mode: "create" | "edit") => {
    setFolderDialog(mode);
    setFolderName(mode === "edit" ? folder?.name || "" : "");
    setAccessMode(
      folder?.access_roles == null
        ? "inherit"
        : folder.access_roles.length || folder.access_users?.length
          ? "custom"
          : "admins",
    );
    setAccessRoles(folder?.access_roles || []);
    setAccessUsers(folder?.access_users || []);
  };
  const saveFolder = () =>
    run(async () => {
      if (folderDialog === "create")
        await api.post("/company-docs/folders", {
          name: folderName,
          parent_id: folderId,
        });
      else
        await api.patch(`/company-docs/folders/${folderId}`, {
          name: folderName,
          access_roles:
            accessMode === "inherit"
              ? null
              : accessMode === "admins"
                ? []
                : accessRoles,
          access_users: accessMode === "custom" ? accessUsers : [],
        });
      await reloadFolders();
      setFolderDialog(null);
      setNotice("Folder saved.");
    });
  const saveDetails = () =>
    run(async () => {
      if (!selected || !edit) return;
      const d = await patchDoc(selected, {
        name: edit.name,
        description: edit.description,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        folder_id: edit.folder_id,
        status: edit.status,
        owner_id: edit.owner_id,
        pinned: edit.pinned,
        review_date: edit.review_date || null,
        expires_on: edit.expires_on || null,
      });
      setSelected(d);
      setEdit(d);
      setNotice("Document details saved.");
    });
  const children = folders
    .filter((f) => f.parent_id === folderId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const folderOnly = view === "folder" && !query && !status;
  const hideEmptyDocuments =
    folderOnly && children.length > 0 && !docs.length && !loading;
  const fileCount = (f: Folder) =>
    f.total_document_count == null
      ? "File count unavailable"
      : `${f.total_document_count} file${f.total_document_count === 1 ? "" : "s"}`;
  const tree = (parent: string | null, depth = 0): React.ReactNode =>
    folders
      .filter((f) => f.parent_id === parent)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((f) => {
        const nested = folders.some((x) => x.parent_id === f.id);
        return (
          <div key={f.id} className="cd-tree-item">
            {nested ? (
              <details
                open={pathTo(folderId).some((x) => x.id === f.id) || undefined}
              >
                <summary>
                  <button
                    aria-label={f.name}
                    className={folderId === f.id ? "active" : ""}
                    onClick={() => navigate(f.id)}
                  >
                    {f.name}
                  </button>
                </summary>
                {tree(f.id, depth + 1)}
              </details>
            ) : (
              <button
                aria-label={f.name}
                className={folderId === f.id ? "active" : ""}
                onClick={() => navigate(f.id)}
              >
                {f.name}
              </button>
            )}
          </div>
        );
      });
  return (
    <div className="company-docs">
      <header className="cd-header">
        <div>
          <div className="cd-eyebrow">MCMULLEN BROWNE • COMPANY LIBRARY</div>
          <h1>Company Docs</h1>
          <p>One organized home for the documents that run our business.</p>
        </div>
        {manage && (
          <div className="cd-actions">
            <button onClick={() => openFolderDialog("create")}>
              ＋ New folder
            </button>
            <button
              className="primary"
              disabled={!folderId || view === "trash" || busy}
              onClick={() => input.current?.click()}
            >
              ↑ Upload files
            </button>
          </div>
        )}
      </header>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => uploadFiles(e.target.files)}
      />
      {error && (
        <div role="alert" className="cd-alert">
          {error}{" "}
          <button
            onClick={() => {
              setError("");
              reloadFolders().catch((e) => setError(errorText(e)));
              reloadDocs();
            }}
          >
            Retry loading
          </button>
        </div>
      )}
      {notice && (
        <div role="status" className="cd-notice">
          {notice}
        </div>
      )}
      <div className="cd-layout">
        <aside className="cd-sidebar">
          <nav aria-label="Library views">
            {[
              ["home", "Library home"],
              ["recent", "Recent documents"],
              ["pinned", "Pinned documents"],
              ...(manage ? [["trash", "Trash"]] : []),
            ].map(([key, title]) => (
              <button
                key={key}
                className={view === key ? "active" : ""}
                onClick={() => navigate(null, key)}
              >
                {title}
              </button>
            ))}
          </nav>
          <div className="cd-tree-label">FOLDER LIBRARY</div>
          <div className="cd-tree">{tree(null)}</div>
          <p className="cd-help">
            {manage
              ? "Admins manage files and folder access."
              : "Showing only folders available to your account."}
          </p>
        </aside>
        <main className="cd-main">
          <div className="cd-toolbar">
            <input
              aria-label="Search company documents"
              placeholder="Search names, descriptions and tags across the library…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
            />
            <select
              aria-label="Filter by status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All statuses</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {label(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="cd-breadcrumb">
            <button onClick={() => navigate(null, "home")}>Company Docs</button>
            {pathTo(folderId).map((f) => (
              <React.Fragment key={f.id}>
                <span> / </span>
                <button onClick={() => navigate(f.id)}>{f.name}</button>
              </React.Fragment>
            ))}
          </div>
          {view === "home" && !query && (
            <div className="cd-welcome">
              <div>
                <h2>Everything has a place.</h2>
                <p>
                  Browse by department, keep approved documents current, and
                  find the right version when you need it.
                </p>
              </div>
              <span>
                {folders.filter((f) => !f.parent_id).length}
                <small>main sections</small>
              </span>
            </div>
          )}
          {manage && !folders.length && !loading && (
            <button
              className="primary"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await api.post("/company-docs/initialize");
                  await reloadFolders();
                  setNotice("Company folder library created.");
                })
              }
            >
              Set up company folders
            </button>
          )}
          <div className="cd-section-title">
            <h2>
              {query
                ? "Search results"
                : folder?.name ||
                  {
                    home: "Company folders",
                    recent: "Recent documents",
                    pinned: "Pinned documents",
                    trash: "Trash",
                  }[view] ||
                  "Documents"}
            </h2>
            {manage && folder && (
              <button onClick={() => openFolderDialog("edit")}>
                Folder settings
              </button>
            )}
          </div>
          {folder && !query && (
            <p className="cd-help">
              {fileCount(folder)} in this folder and its subfolders.{" "}
              {children.length > 0 &&
                "Choose a subfolder below to browse its files."}
            </p>
          )}
          {!query && (view === "home" || view === "folder") && (
            <div className="cd-folders">
              {children.map((f) => (
                <button
                  key={f.id}
                  className="cd-folder-card"
                  onClick={() => navigate(f.id)}
                >
                  <span className="cd-folder-icon">📁</span>
                  <strong>{f.name}</strong>
                  <small>
                    {f.access_roles !== undefined && f.access_roles !== null
                      ? "Restricted access"
                      : "Folder"}{" "}
                    · {fileCount(f)} ·{" "}
                    {folders.filter((x) => x.parent_id === f.id).length}{" "}
                    subfolders
                  </small>
                </button>
              ))}
            </div>
          )}
          {view === "trash" && (
            <p className="cd-help">
              Trashed files keep their version history. Restore them to make
              them available again. There is no automatic permanent deletion.
            </p>
          )}
          {!hideEmptyDocuments && (
            <div className="cd-section-title">
              <h3>
                {view === "home" && !query
                  ? "Recently updated files"
                  : `${total} document${total === 1 ? "" : "s"}${folderOnly ? " directly in this folder" : ""}`}
              </h3>
              {busy && <span role="status">Working…</span>}
            </div>
          )}
          {loading ? (
            <p role="status">Loading library…</p>
          ) : docs.length ? (
            <div className="cd-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Status</th>
                    <th>Version</th>
                    <th>Updated</th>
                    <th>Review / Expiration</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <button
                          className="cd-doc-name"
                          onClick={() => openDoc(doc)}
                        >
                          {doc.pinned ? "★ " : ""}
                          {doc.name}
                        </button>
                        <small>
                          {pathTo(doc.folder_id)
                            .map((f) => f.name)
                            .join(" / ")}
                        </small>
                        {doc.tags.length > 0 && (
                          <small>{doc.tags.join(" · ")}</small>
                        )}
                      </td>
                      <td>
                        <span className={`cd-status ${doc.status}`}>
                          {label(doc.status)}
                        </span>
                      </td>
                      <td>v{doc.version_count}</td>
                      <td>{date(doc.updated_at)}</td>
                      <td>
                        {doc.review_date && (
                          <small>Review {date(doc.review_date)}</small>
                        )}
                        {doc.expires_on && (
                          <small
                            className={
                              doc.expires_on <
                              new Date().toISOString().slice(0, 10)
                                ? "cd-expired"
                                : ""
                            }
                          >
                            Expires {date(doc.expires_on)}
                          </small>
                        )}
                        {!doc.review_date && !doc.expires_on ? "—" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : hideEmptyDocuments ? null : (
            <div className="cd-empty">
              <span>📄</span>
              <h3>
                {query || status
                  ? "No matching documents"
                  : view === "trash"
                    ? "Trash is empty"
                    : "No documents here yet"}
              </h3>
              <p>
                {query || status
                  ? "Try a different search or status filter."
                  : manage
                    ? folderId
                      ? "Upload the first file to this folder."
                      : "Choose a folder above to start filing company documents."
                    : "Approved company files will appear here when your team adds them."}
              </p>
            </div>
          )}
          {total > 100 && (
            <div className="cd-actions">
              <button disabled={!page} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {page + 1} of {Math.ceil(total / 100)}
              </span>
              <button
                disabled={(page + 1) * 100 >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </main>
      </div>
      {folderDialog && (
        <div className="cd-overlay">
          <section
            className="cd-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={
              folderDialog === "create" ? "New folder" : "Folder settings"
            }
          >
            <h2>
              {folderDialog === "create" ? "New folder" : "Folder settings"}
            </h2>
            <label>
              Folder name
              <input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                maxLength={160}
              />
            </label>
            {folderDialog === "create" ? (
              <p className="cd-help">
                {folder
                  ? "Created inside " +
                    folder.name +
                    " and inherits its access."
                  : "New top-level folders start restricted to admins."}
              </p>
            ) : (
              <>
                <label>
                  Who can view this folder?
                  <select
                    value={accessMode}
                    onChange={(e) => setAccessMode(e.target.value)}
                  >
                    <option value="inherit">
                      Inherit parent access (all staff at the top level)
                    </option>
                    <option value="admins">Admins only</option>
                    <option value="custom">Selected roles and people</option>
                  </select>
                </label>
                <p className="cd-help">
                  Admins always have access. Parent restrictions also apply to
                  every subfolder, file and version.
                </p>
                {accessMode === "custom" && (
                  <>
                    <h3>Roles</h3>
                    <div className="cd-checks">
                      {roles.map((r) => (
                        <label key={r}>
                          <input
                            type="checkbox"
                            checked={accessRoles.includes(r)}
                            onChange={(e) =>
                              setAccessRoles((old) =>
                                e.target.checked
                                  ? [...old, r]
                                  : old.filter((x) => x !== r),
                              )
                            }
                          />
                          {label(r)}
                        </label>
                      ))}
                    </div>
                    <h3>Specific people</h3>
                    <div className="cd-checks">
                      {people
                        .filter((p) => p.role !== "admin")
                        .map((p) => (
                          <label key={p.id}>
                            <input
                              type="checkbox"
                              checked={accessUsers.includes(p.id)}
                              onChange={(e) =>
                                setAccessUsers((old) =>
                                  e.target.checked
                                    ? [...old, p.id]
                                    : old.filter((x) => x !== p.id),
                                )
                              }
                            />
                            {p.name} · {label(p.role)}
                          </label>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}
            <div className="cd-actions">
              <button disabled={busy} onClick={() => setFolderDialog(null)}>
                Cancel
              </button>
              <button
                className="primary"
                disabled={busy || !folderName.trim()}
                onClick={saveFolder}
              >
                Save folder
              </button>
            </div>
            {error && (
              <p role="alert" className="cd-expired">
                {error}
              </p>
            )}
          </section>
        </div>
      )}
      {selected && edit && (
        <div className="cd-overlay">
          <section
            className="cd-dialog cd-detail"
            role="dialog"
            aria-modal="true"
            aria-label="Document details"
          >
            <div className="cd-section-title">
              <h2>{selected.name}</h2>
              <button onClick={closeDoc}>Close</button>
            </div>
            {selected.deleted_at && (
              <p className="cd-alert">
                This document is in Trash. Restore it before previewing or
                downloading.
              </p>
            )}
            {manage ? (
              <>
                <div className="cd-form-grid">
                  <label>
                    Name
                    <input
                      value={edit.name}
                      onChange={(e) =>
                        setEdit({ ...edit, name: e.target.value })
                      }
                      maxLength={240}
                    />
                  </label>
                  <label>
                    Folder
                    <select
                      value={edit.folder_id}
                      onChange={(e) =>
                        setEdit({ ...edit, folder_id: e.target.value })
                      }
                    >
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>
                          {pathTo(f.id)
                            .map((x) => x.name)
                            .join(" / ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Status
                    <select
                      value={edit.status}
                      onChange={(e) =>
                        setEdit({ ...edit, status: e.target.value })
                      }
                    >
                      {statuses.map((s) => (
                        <option key={s} value={s}>
                          {label(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Document owner
                    <select
                      value={edit.owner_id || ""}
                      onChange={(e) =>
                        setEdit({ ...edit, owner_id: e.target.value || null })
                      }
                    >
                      <option value="">Unassigned</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Review date
                    <input
                      type="date"
                      value={edit.review_date || ""}
                      onChange={(e) =>
                        setEdit({ ...edit, review_date: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Expiration date
                    <input
                      type="date"
                      value={edit.expires_on || ""}
                      onChange={(e) =>
                        setEdit({ ...edit, expires_on: e.target.value })
                      }
                    />
                  </label>
                </div>
                <label>
                  Description
                  <textarea
                    value={edit.description}
                    onChange={(e) =>
                      setEdit({ ...edit, description: e.target.value })
                    }
                  />
                </label>
                <label>
                  Tags, separated by commas
                  <input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                  />
                </label>
                <label className="cd-inline">
                  <input
                    type="checkbox"
                    checked={edit.pinned}
                    onChange={(e) =>
                      setEdit({ ...edit, pinned: e.target.checked })
                    }
                  />
                  Pin in the company library
                </label>
                <div className="cd-actions">
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={saveDetails}
                  >
                    Save details
                  </button>
                  {selected.deleted_at ? (
                    <button
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await patchDoc(selected, { trashed: false });
                          closeDoc();
                          setNotice("Document restored.");
                        })
                      }
                    >
                      Restore document
                    </button>
                  ) : (
                    <>
                      <button
                        disabled={busy}
                        onClick={() => replacement.current?.click()}
                      >
                        Upload new version
                      </button>
                      <button
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await patchDoc(selected, { trashed: true });
                            closeDoc();
                            setNotice(
                              "Moved to Trash. You can restore it anytime.",
                            );
                          })
                        }
                      >
                        Move to Trash
                      </button>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  hidden
                  ref={replacement}
                  onChange={(e) => uploadFiles(e.target.files, true)}
                />
              </>
            ) : (
              <>
                <p>{selected.description}</p>
                <span className="cd-status">{label(selected.status)}</span>
              </>
            )}
            {error && (
              <p role="alert" className="cd-expired">
                {error}
              </p>
            )}
            <h3>Version history</h3>
            <p className="cd-help">
              New uploads preserve earlier files and return the document to
              Draft.
            </p>
            {detailLoading ? (
              <p role="status">Loading versions…</p>
            ) : versions.length ? (
              versions.map((v) => (
                <div className="cd-version" key={v.id}>
                  <div>
                    <strong>Version {v.version_number}</strong> · {v.file_name}
                    <small>
                      {date(v.created_at)} · {size(v.file_size)}
                    </small>
                  </div>
                  <div className="cd-actions">
                    <button
                      disabled={busy || !!selected.deleted_at}
                      onClick={() => downloadFile(v)}
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>
                No file versions were saved. Upload a file after restoring this
                record if needed.
              </p>
            )}
            {preview && (
              <div className="cd-preview">
                {preview.type.startsWith("image/") ? (
                  <img alt="Document preview" src={preview.url} />
                ) : preview.type === "application/pdf" ? (
                  <object
                    aria-label="PDF document preview"
                    data={preview.url}
                    type="application/pdf"
                  >
                    <p>
                      Your browser cannot display this PDF. Use Download above
                      to open the original file.
                    </p>
                  </object>
                ) : (
                  <iframe
                    title="Document preview"
                    src={preview.url}
                    sandbox=""
                  />
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
